import { markErrorAsHandled } from '@confluence/graphql';
import { isUnauthorizedError } from '@confluence/error-boundary';

import { i18n } from '../commentsI18n';

import { isCreateCommentAllowed, fetchCommentVersion, fetchComment } from './utils';
import { EDITOR_MODE, STATUS_CODES, ERROR_TYPES } from './constants';

const sendAnalyticEvent = ({ callAnalyticsEvent, attributes }) => {
	callAnalyticsEvent({
		data: {
			source: 'viewPage',
			action: 'failed',
			actionSubject: 'commentCreation',
			actionSubjectId: 'pageComment',
			attributes,
		},
		type: 'sendOperationalEvent',
	});
};

export const processAndFetchErrorDetails = async ({
	error,
	canAddComments,
	callAnalyticsEvent,
	contentId,
	mode,
	commentId,
	version: currentCommentVersion,
}) => {
	let errorType = ERROR_TYPES.UNHANDLED_ERROR;
	let formattedErrorMessage = i18n.genericError;
	let attributes = {};

	/**
  403 - User doees not have permission to add comments
  400 - User is replying to a comment that is already deleted
  409 - User is Editing a comment which is not in sync with back-end - Version numbers are different
  404 - User is Editing  a comment that is already deleted

  Note: - The above errors are very rare and happen when multiple users are attempting to work on the same comment
  **/

	const errorCode = error?.graphQLErrors?.[0]?.originalError?.response?.status ?? '';

	switch (errorCode) {
		//429
		case STATUS_CODES.RATE_LIMITING_ERROR:
			errorType = ERROR_TYPES.KNOWN_ERROR;
			formattedErrorMessage = i18n.rateLimitError;
			markErrorAsHandled(error);
			attributes = {
				errorCode,
			};
			sendAnalyticEvent({ callAnalyticsEvent, attributes });
			break;
		//409
		case STATUS_CODES.CONFLICT_ERROR:
			if (mode === EDITOR_MODE.EDIT) {
				const latestCommentVersion = await fetchCommentVersion(commentId);
				//When we edit a comment we add current version + 1 and pass in payload
				//In case of a stale comment the version number we have is not the latest so when we add +1 it will equal
				//the version in back-end and hence result in 409 conflict.
				if (
					latestCommentVersion &&
					currentCommentVersion['number'] <= latestCommentVersion['number']
				) {
					errorType = ERROR_TYPES.KNOWN_ERROR;
					formattedErrorMessage = i18n.editVersionError;
					markErrorAsHandled(error);
				}

				attributes = {
					commentVersionInPayload: currentCommentVersion['number'],
					commentVersionInServer: latestCommentVersion && latestCommentVersion['number'],
					errorCode,
				};

				sendAnalyticEvent({ callAnalyticsEvent, attributes });
			}
			break;
		//400
		case STATUS_CODES.BAD_REQUEST_ERROR:
			if (mode === EDITOR_MODE.REPLY) {
				// fetch the comment the user is replying to(parent) and verify if it exists
				const replyToComment = await fetchComment(commentId);

				if (!replyToComment) {
					errorType = ERROR_TYPES.KNOWN_ERROR;
					formattedErrorMessage = i18n.replyError;
					markErrorAsHandled(error);
				}

				attributes = {
					originalReplyToCommentId: commentId,
					latestReplyToCommentId: replyToComment ? replyToComment.id : '',
					errorCode,
				};

				sendAnalyticEvent({ callAnalyticsEvent, attributes });
			}
			break;
		//403
		case STATUS_CODES.PERMISSIONS_ERROR:
			let isUserUnauthorized, isUserEligibleToAddComments, errorMessage;
			let isExpectedErrorCase = false;

			try {
				isUserEligibleToAddComments = await isCreateCommentAllowed(contentId);
				/** If below condition is met, it indicates users permissions got updated while they are on the page or
        user is on a stale page i.e not refreshed and is possibly looking at cached data  **/
				if (canAddComments && !isUserEligibleToAddComments) {
					errorType = ERROR_TYPES.KNOWN_ERROR;
					formattedErrorMessage = i18n.permissionsError;
					markErrorAsHandled(error);
					isExpectedErrorCase = true;
				}
			} catch (err) {
				/** Graphql return an error Object indicating user is not authorized due to their session expired
        as per https://pug.jira-dev.com/wiki/spaces/CCB/pages/4709260932/GraphQL+Errors+Contract+for+Status+Code+401+and+403  **/
				if (isUnauthorizedError(err)) {
					errorType = ERROR_TYPES.KNOWN_ERROR;
					formattedErrorMessage = i18n.userUnauthorizedError;
					isUserUnauthorized = true;
					markErrorAsHandled(err);
					isExpectedErrorCase = true;
				}
				errorMessage = err;
			}

			if (!isExpectedErrorCase) {
				// General error case catch from https://product-fabric.atlassian.net/browse/COMMENTS-726
				errorType = ERROR_TYPES.KNOWN_ERROR;
				formattedErrorMessage = i18n.permissionsError;
				markErrorAsHandled(error);
			}

			attributes = {
				originalPermission: canAddComments,
				latestPermission: isUserEligibleToAddComments,
				errorCode,
				isUserUnauthorized,
				errorMessage, //add errorMessage for debugging if CommentPermissionsQuery fails
			};

			sendAnalyticEvent({ callAnalyticsEvent, attributes });
			break;
		case STATUS_CODES.NOT_FOUND_ERROR:
			if (mode === EDITOR_MODE.EDIT) {
				// fetch the comment the user is editing
				const editedComment = await fetchComment(commentId);
				if (!editedComment) {
					errorType = ERROR_TYPES.KNOWN_ERROR;
					formattedErrorMessage = i18n.editNotFoundError;
					markErrorAsHandled(error);
				}

				attributes = {
					originalEditCommentId: commentId,
					latestEditCommentId: editedComment ? editedComment.id : '',
					errorCode,
				};

				sendAnalyticEvent({ callAnalyticsEvent, attributes });
			}
			break;
		// 401
		case STATUS_CODES.UNAUTHORIZED_ERROR:
			// General error case catch from https://product-fabric.atlassian.net/browse/COMMENTS-727
			errorType = ERROR_TYPES.KNOWN_ERROR;
			formattedErrorMessage = i18n.userUnauthorizedError;
			markErrorAsHandled(error);

			attributes = {
				errorCode,
			};

			sendAnalyticEvent({ callAnalyticsEvent, attributes });

			break;
		default:
			break;
	}

	return { errorType, formattedErrorMessage };
};
